/**
 * Product schema — REQ-20260520 ERP Phase 1C。
 * 從 mdb 102 欄抽 25 欄常用;5 級分售價、3 個成本欄(SALES 不可見)、庫存控制。
 */
import { z } from 'zod';

const decimalString = z
  .union([z.string(), z.number()])
  .transform((v) => (typeof v === 'number' ? String(v) : v))
  .refine((s) => /^-?\d+(\.\d+)?$/.test(s.trim()), '金額格式不正確')
  .transform((s) => s.trim());

const baseFields = {
  code: z.string().trim().min(1, '貨品編號必填').max(20),
  name: z.string().trim().min(1, '品名必填').max(60),
  spec: z.string().trim().max(60).nullable().optional(),
  usage: z.string().trim().max(60).nullable().optional(),
  kind: z.string().trim().max(20).nullable().optional(),
  barcode: z.string().trim().max(40).nullable().optional(),
  oemCode: z.string().trim().max(20).nullable().optional(),

  categoryId: z.coerce.number().int().positive().nullable().optional(),
  unitId: z.coerce.number().int().positive().nullable().optional(),
  defaultSupplierId: z.coerce.number().int().positive().nullable().optional(),
  defaultWarehouseId: z.coerce.number().int().positive().nullable().optional(),

  // 售價(5 級分)
  listPrice: decimalString.nullable().optional(),
  marketPrice: decimalString.nullable().optional(),
  priceTierA: decimalString.nullable().optional(),
  priceTierB: decimalString.nullable().optional(),
  priceTierC: decimalString.nullable().optional(),
  priceTierD: decimalString.nullable().optional(),
  priceTierE: decimalString.nullable().optional(),

  // 成本(SALES 看不到。Backend 收到 SALES 的 input 會忽略這幾個欄位)
  costStandard: decimalString.nullable().optional(),
  costCurrent: decimalString.nullable().optional(),
  costMonthAvg: decimalString.nullable().optional(),

  // 庫存
  trackStock: z.boolean().default(true),
  safetyEnabled: z.boolean().default(false),
  safetyStock: decimalString.nullable().optional(),

  notes: z.string().trim().max(5000).nullable().optional(),
};

export const CreateProductSchema = z.object(baseFields);
export type CreateProductInput = z.infer<typeof CreateProductSchema>;

export const UpdateProductSchema = z.object(baseFields).partial();
export type UpdateProductInput = z.infer<typeof UpdateProductSchema>;

export const ProductViewSchema = z.object({
  id: z.number().int(),
  uuid: z.string(),
  code: z.string(),
  name: z.string(),
  spec: z.string().nullable(),
  usage: z.string().nullable(),
  kind: z.string().nullable(),
  barcode: z.string().nullable(),
  oemCode: z.string().nullable(),

  categoryId: z.number().int().nullable(),
  categoryName: z.string().nullable(),
  unitId: z.number().int().nullable(),
  unitName: z.string().nullable(),
  defaultSupplierId: z.number().int().nullable(),
  defaultSupplierName: z.string().nullable(),
  defaultWarehouseId: z.number().int().nullable(),
  defaultWarehouseName: z.string().nullable(),

  listPrice: z.string().nullable(),
  marketPrice: z.string().nullable(),
  priceTierA: z.string().nullable(),
  priceTierB: z.string().nullable(),
  priceTierC: z.string().nullable(),
  priceTierD: z.string().nullable(),
  priceTierE: z.string().nullable(),

  // 成本欄位:SALES 收到 null(由 backend 過濾)
  costStandard: z.string().nullable(),
  costCurrent: z.string().nullable(),
  costMonthAvg: z.string().nullable(),
  /** 是否回了完整成本欄位(SALES 角色為 false) */
  costsVisible: z.boolean(),

  trackStock: z.boolean(),
  safetyEnabled: z.boolean(),
  safetyStock: z.string().nullable(),

  lastPurchaseAt: z.string().datetime().nullable(),
  lastSaleAt: z.string().datetime().nullable(),
  lastSalePrice: z.string().nullable(),
  lastCostPrice: z.string().nullable(),

  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ProductView = z.infer<typeof ProductViewSchema>;

export const ProductListQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(25),
  q: z.string().trim().max(100).optional(),
  categoryId: z.coerce.number().int().positive().optional(),
  unitId: z.coerce.number().int().positive().optional(),
  supplierId: z.coerce.number().int().positive().optional(),
  sort: z.enum(['code', 'name', 'updatedAt', 'createdAt']).default('updatedAt'),
  order: z.enum(['asc', 'desc']).default('desc'),
});
export type ProductListQuery = z.infer<typeof ProductListQuerySchema>;

export const ProductListItemSchema = z.object({
  id: z.number().int(),
  code: z.string(),
  name: z.string(),
  spec: z.string().nullable(),
  categoryName: z.string().nullable(),
  unitName: z.string().nullable(),
  listPrice: z.string().nullable(),
  defaultSupplierName: z.string().nullable(),
  updatedAt: z.string().datetime(),
});
export type ProductListItem = z.infer<typeof ProductListItemSchema>;
